import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  PenLine,
  Users,
  FileText,
  BarChart3,
  ArrowRight,
  Sparkles,
  LucideIcon,
} from "lucide-react";

interface QuickAction {
  title: string;
  description: string;
  icon: LucideIcon;
  href: string;
  color: string;
  bgColor: string;
}

const quickActions: QuickAction[] = [
  {
    title: "Create Newsletter",
    description: "Compose a new email",
    icon: PenLine,
    href: "/newsletter/create",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10 group-hover:bg-blue-500/20",
  },
  {
    title: "View Users",
    description: "Manage user accounts",
    icon: Users,
    href: "/users",
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10 group-hover:bg-emerald-500/20",
  },
  {
    title: "Manage Content",
    description: "Edit articles & briefs",
    icon: FileText,
    href: "/content",
    color: "text-violet-500",
    bgColor: "bg-violet-500/10 group-hover:bg-violet-500/20",
  },
  {
    title: "View Analytics",
    description: "Performance metrics",
    icon: BarChart3,
    href: "/analytics",
    color: "text-amber-500",
    bgColor: "bg-amber-500/10 group-hover:bg-amber-500/20",
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 24,
    },
  },
};

function QuickActionCard({ action }: { action: QuickAction }) {
  const navigate = useNavigate();
  const Icon = action.icon;

  return (
    <motion.div
      variants={item}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate(action.href)}
      className="cursor-pointer"
    >
      <Card className="h-full group hover:shadow-md transition-all duration-300 border-border/50 hover:border-border overflow-hidden relative">
        {/* Subtle gradient background on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <CardContent className="p-5 relative">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "p-3 rounded-xl transition-colors duration-300",
                  action.bgColor
                )}
              >
                <Icon className={cn("w-5 h-5", action.color)} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-0.5 group-hover:text-primary transition-colors">
                  {action.title}
                </h3>
                <p className="text-sm text-muted-foreground">{action.description}</p>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              whileHover={{ x: 0 }}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </motion.div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function QuickActions() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Quick Actions</h2>
      </div>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {quickActions.map((action) => (
          <QuickActionCard key={action.title} action={action} />
        ))}
      </motion.div>
    </div>
  );
}
